
import React, { useState, useEffect } from 'react';
// FIX: Use namespace import for react-router-dom to fix potential module resolution errors.
import * as ReactRouterDom from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ObrasPage } from './pages/ObrasPage';
import { ObraDetailPage } from './pages/ObraDetailPage'; 
import { CostCategoryDetailPage } from './pages/CostCategoryDetailPage';
import { SubItemDetailPage } from './pages/SubItemDetailPage';
import { ExpenseDetailPage } from './pages/ExpenseDetailPage';
import RelatorioPage from './pages/RelatorioPage';
// import { PrintReportPage } from './pages/PrintReportPage'; // Removed obsolete import
import { EmpresasPage } from './pages/EmpresasPage'; 
import { CadastrarUsuarioPage } from './pages/CadastrarUsuarioPage';
import { ConfiguracoesPage } from './pages/ConfiguracoesPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LembretesPage } from './pages/LembretesPage';
import { ReminderNotification } from './components/ReminderNotification';
import { useAuth } from './hooks/useAuth';
import { useNotifications } from './hooks/useNotifications';
import { MissingEntriesModal } from './components/MissingEntriesModal';
import { ImportacaoPage } from './pages/ImportacaoPage';
import { API_BASE_URL, forceHttpsUrl } from './constants';

// Layout for pages with sidebar and header, uses Outlet for nested routes
const MainLayout: React.FC = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    useEffect(() => {
        try {
            const storedSidebarState = localStorage.getItem('sidebarOpen');
            if (storedSidebarState) {
                setIsSidebarOpen(JSON.parse(storedSidebarState));
            }
        } catch (error) {
            console.error("Failed to get or parse sidebar state, resetting to default.", error);
            try {
                localStorage.removeItem('sidebarOpen'); // Clean up potentially corrupted data
            } catch (removeError) {
                console.error("Failed to remove corrupted sidebar state from localStorage.", removeError);
            }
            setIsSidebarOpen(true);
        }
    }, []);

    const toggleSidebar = () => {
        setIsSidebarOpen(prev => {
            const newState = !prev;
            try {
                localStorage.setItem('sidebarOpen', JSON.stringify(newState));
            } catch (error) {
                console.error("Failed to save sidebar state to localStorage.", error);
            }
            return newState;
        });
    };

    return (
        <div className="flex h-screen bg-gray-50 dark:bg-dark-bg text-gray-800 dark:text-dark-text-primary">
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
            <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-20'}`}>
                <Header />
                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-dark-bg p-4 sm:p-6 lg:p-8">
                    <ReactRouterDom.Outlet /> {/* Child routes render here */}
                </main>
            </div>
        </div>
    );
};

// A wrapper for routes that require authentication.
const ProtectedRoute: React.FC = () => {
  const { user } = useAuth();
  const location = ReactRouterDom.useLocation();

  if (!user) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to. This allows us to send them along to that page after they login.
    return <ReactRouterDom.Navigate to="/login" state={{ from: location }} replace />;
  }

  return <ReactRouterDom.Outlet />; // Render the nested routes, which can include another layout
};


const AppRoutes: React.FC = () => {
    const { user, loading } = useAuth();

    if (loading) {
        // Render a blank screen instead of a loading message to provide a cleaner/faster perceived load.
        return <div className="h-screen w-screen bg-gray-50 dark:bg-dark-bg" />;
    }
    
    return (
        <ReactRouterDom.Routes>
            {/* Public route */}
            <ReactRouterDom.Route path="/login" element={user ? <ReactRouterDom.Navigate to="/dashboard" replace /> : <LoginPage />} />

            {/* Protected routes */}
            <ReactRouterDom.Route element={<ProtectedRoute />}>
                <ReactRouterDom.Route element={<MainLayout />}>
                    <ReactRouterDom.Route path="/" element={<ReactRouterDom.Navigate to="/dashboard" replace />} />
                    <ReactRouterDom.Route path="dashboard" element={<DashboardPage />} />
                    <ReactRouterDom.Route path="obras" element={<ObrasPage />} />
                    <ReactRouterDom.Route path="obras/:obraId" element={<ObraDetailPage />} />
                    <ReactRouterDom.Route path="obras/:obraId/custos/:categoryId" element={<CostCategoryDetailPage />} />
                    <ReactRouterDom.Route path="obras/:obraId/custos/:categoryId/:subItemId" element={<SubItemDetailPage />} />
                    <ReactRouterDom.Route path="obras/:obraId/custos/:categoryId/:subItemId/:expenseId" element={<ExpenseDetailPage />} />
                    <ReactRouterDom.Route path="lembretes" element={<LembretesPage />} />
                    <ReactRouterDom.Route path="cadastrar-empresas" element={<EmpresasPage />} />
                    <ReactRouterDom.Route path="cadastrar-usuario" element={<CadastrarUsuarioPage />} />
                    <ReactRouterDom.Route path="configuracoes" element={<ConfiguracoesPage />} />
                    <ReactRouterDom.Route path="importacao" element={<ImportacaoPage />} />
                </ReactRouterDom.Route>
                {/* Protected route without layout */}
                <ReactRouterDom.Route path="obras/:obraId/relatorio" element={<RelatorioPage />} />
            </ReactRouterDom.Route>

            {/* Catch-all route */}
            <ReactRouterDom.Route path="*" element={<NotFoundPage />} />
        </ReactRouterDom.Routes>
    );
};

const AppWrapper: React.FC = () => {
    const { isModalOpen, hideMissingEntriesModal, modalContent } = useNotifications();

    return (
        <>
            <ReminderNotification />
            <AppRoutes />
            <MissingEntriesModal isOpen={isModalOpen} onClose={hideMissingEntriesModal} content={modalContent} />
        </>
    );
};

// Helper function to force favicon update by removing and re-adding all icon links.
// This is more aggressive and reliable for bypassing browser cache.
const updateFavicon = (url: string) => {
    const head = document.head;
    const oldLinks = document.querySelectorAll("link[rel*='icon']");
    
    // Remove all existing favicon links to force a refresh
    oldLinks.forEach(link => head.removeChild(link));

    // Create and append a new favicon link
    const link = document.createElement('link');
    link.id = 'favicon'; // Keep the id for future reference if needed
    link.rel = 'icon';
    link.href = url;
    
    // Try to set the type attribute from the data URI for better browser compatibility
    try {
        const mimeType = url.substring(url.indexOf(':') + 1, url.indexOf(';'));
        if (mimeType.startsWith('image/')) {
            link.type = mimeType;
        }
    } catch (e) {
        // It's okay if this fails, the browser can often infer the type.
    }

    head.appendChild(link);
};


const App: React.FC = () => {
    useEffect(() => {
        const setDefaultFavicon = () => {
            const defaultFaviconSvg = `<svg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'><rect width='100' height='100' fill='white'/><g transform='translate(0, 20)'><polygon points='0,60 30,0 42,20 18,60' fill='%23006781'/><polygon points='38,0 62,0 50,25' fill='%23006781'/><polygon points='58,22 70,0 100,60 80,60' fill='%237EC400'/><polygon points='30,0 38,0 44,15 42,20' fill='%23FFFFFF'/><polygon points='62,0 70,0 60,18 58,22' fill='%23FFFFFF'/><polygon points='42,20 50,25 58,22 50,28' fill='%23FFFFFF'/></g></svg>`;
            updateFavicon(`data:image/svg+xml,${encodeURIComponent(defaultFaviconSvg)}`);
        };

        const fetchAndApplySettings = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/settings`);
                
                if (response.ok) {
                    const settings = await response.json();
                    
                    // Handle Favicon
                    if (settings.favicon_url) {
                        updateFavicon(forceHttpsUrl(settings.favicon_url)!);
                    } else {
                        // Only set default if no favicon is set from the server and none is in cache
                        if (!document.getElementById('favicon')?.getAttribute('href')?.startsWith('data:image/')) {
                           setDefaultFavicon();
                        }
                    }

                    // Handle Logos and cache in localStorage
                    if (settings.logo_full_url) {
                        localStorage.setItem('lam-custos-logo-full', forceHttpsUrl(settings.logo_full_url)!);
                    } else {
                        localStorage.removeItem('lam-custos-logo-full');
                    }
                    if (settings.logo_icon_url) {
                        localStorage.setItem('lam-custos-logo-icon', forceHttpsUrl(settings.logo_icon_url)!);
                    } else {
                        localStorage.removeItem('lam-custos-logo-icon');
                    }
                } else {
                    // On API error, DO NOT clear localStorage. Keep the old logos if they exist.
                    console.error("Failed to fetch settings from API, using cached version if available.");
                    const cachedFavicon = localStorage.getItem('lam-custos-favicon');
                     if (cachedFavicon) {
                        updateFavicon(forceHttpsUrl(cachedFavicon)!);
                    } else if (!document.getElementById('favicon')?.getAttribute('href')?.startsWith('data:image/')) {
                       setDefaultFavicon();
                    }
                }
            } catch (error) {
                // On network error, also DO NOT clear localStorage.
                console.error("Failed to fetch system settings due to network error, using cached version if available.", error);
                 const cachedFavicon = localStorage.getItem('lam-custos-favicon');
                 if (cachedFavicon) {
                    updateFavicon(forceHttpsUrl(cachedFavicon)!);
                } else if (!document.getElementById('favicon')?.getAttribute('href')?.startsWith('data:image/')) {
                   setDefaultFavicon();
                }
            }
        };

        fetchAndApplySettings();

    }, []);

  return (
    <ReactRouterDom.HashRouter>
      <AppWrapper />
    </ReactRouterDom.HashRouter>
  );
};

export default App;